/* eslint-disable lines-between-class-members */
import Component from '@glimmer/component';
import { action, computed, set } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { inject as service } from '@ember/service';
import { A } from '@ember/array';
import GroupStore from 'mewe/stores/group-store';
import { ds } from 'mewe/stores/ds';
import GroupUtils from 'mewe/utils/group-utils-light';
import { Target } from 'mewe/constants';
import fetchEvents from 'mewe/fetchers/fetch-events';
import { search } from './utils';
import dispatcher from 'mewe/dispatcher';
import { addObserver, removeObserver } from '@ember/object/observers';

export default class MwShareWithPicker extends Component {
  @service router;
  @service account;
  @service dynamicDialogs;

  @tracked searchValue = '';
  @tracked searchResults;
  @tracked maxResults = 100;

  element;

  constructor() {
    super(...arguments);
    this.groupsState = GroupStore.getState();
    this.eventsState = ds.events.for('upcoming-events');

    if (this.args.target == Target.EVENT) {
      if (!this.eventsState.items.length) {
        fetchEvents('events', 'upcoming-events');
      }
    }

    addObserver(this, 'shareWithType', this.resetSearch);
  }

  @action
  onDestroy() {
    removeObserver(this, 'shareWithType', this.resetSearch);
  }

  @computed('args.target', 'properGroups')
  get groups() {
    if (this.args.target !== Target.GROUP) {
      return A();
    } else {
      return A([
        ...this.properGroups.map((g) => {
          return {
            id: g.id,
            name: g.name,
            profileImage400x400FullUrl: g.profileImage400x400FullUrl,
            _links: g._links,
            isSelected: false,
          };
        }),
      ]);
    }
  }

  @action
  doSearch(searchValue) {
    const str = searchValue.toLowerCase().trim();

    if (str.length) {
      const selectedItems = this.targets.filterBy('isSelected', true);
      this.searchResults = search(str, this.args.target, selectedItems, this.args.administrated, this.groups);
    } else {
      this.searchResults = null;
    }
  }

  @computed('searchResults.isPending')
  get loading() {
    return this.searchResults?.isPending;
  }

  resetSearch() {
    this.searchValue = '';
  }

  @computed('args.target', 'searchValue')
  get style() {
    switch (this.args.target) {
      case Target.PAGE:
        return 'style-page';
      case Target.GROUP:
        return 'style-group';
      case Target.EVENT:
        return 'style-event';
      default:
        return '';
    }
  }

  @computed('args.target')
  get placeholderText() {
    switch (this.args.target) {
      case Target.GROUP:
        return __('Search Groups');
      case Target.EVENT:
        return __('Search Events');
      case Target.PAGE:
        return __('Search Pages');
      default:
        return '';
    }
  }

  @computed('groupsState.confirmedGroups')
  get properGroups() {
    return GroupUtils.getPermittedToPostGroups(this.groupsState.confirmedGroups, false);
  }

  @computed('args.administrated.items', 'args.target', 'eventsState.items.length', 'groups')
  get targets() {
    let targets = [];

    switch (this.args.target) {
      case Target.PAGE:
        targets = this.args.administrated?.items;
        break;
      case Target.GROUP:
        targets = this.groups;
        break;
      case Target.EVENT:
        targets = this.eventsState?.items;
        break;
    }

    let selectedTarget = targets.find((t) => t.id === this.args.selectedTargetId);
    if (selectedTarget) {
      selectedTarget.isSelected = true;
    }

    return targets;
  }

  @computed('targets', 'maxResults', 'searchResults.{content,isFulfilled}', 'searchValue.length')
  get targetsDisplay() {
    const isSearchValue = this.searchValue.length !== 0;
    const targetsToDisplay = this.searchResults && isSearchValue ? this.searchResults?.content : this.targets;
    return targetsToDisplay.slice(0, this.maxResults);
  }

  @action
  unSelectContact(clicked) {
    this.updateTargets(clicked);
    this.args.unSelectContact?.(clicked);
  }

  @action
  setShareWithType(type) {
    this.resetSearch();
    this.shareWithType = type;
  }

  @action
  closeSearch() {
    this.resetSearch();
  }

  @action
  afterEventsTransitionFromPicker() {
    this.args.afterEventsTransitionAction?.();
  }

  @action
  goToEvents() {
    this.args.afterEventsTransitionAction?.();
    this.router.transitionTo('app.events');
    this.dynamicDialogs.closeAll();
  }

  @action
  openInvitationsDialog() {
    dispatcher.dispatch('contact', 'openContactInvitationDialog');
  }

  @action
  onScroll() {
    if (this.args.target === Target.EVENT) {
      fetchEvents('events', 'upcoming-events', true);
    }

    if (this.args.target === Target.GROUP) {
      // virtual pagination, we don't show more than 100 results at first to avoid rendering lag
      this.maxResults += 100;
    }
  }

  @action
  updateTargets(clickedItem) {
    const item = this.targets.find((t) => t.id === clickedItem.id);
    const searchItem = this.searchResults?.content.find((c) => c.id === clickedItem.id);

    if (item) {
      this.targets.forEach((t) => set(t, 'isSelected', t.id === item.id));
    }
    if (searchItem) {
      this.searchResults.content.forEach((t) => set(t, 'isSelected', t.id === searchItem.id));
    }

    this.args.onSelect(item || searchItem);
  }
}
